import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

export default function TutorStudents({ apiBase, token }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("roster"); // "roster" | "classes" | "calendar"
  const [students, setStudents] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [tutorProfile, setTutorProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Roster Filter State ("all" | "active" | "inactive")
  const [rosterFilter, setRosterFilter] = useState("all");
  const [openStatusMenuId, setOpenStatusMenuId] = useState(null);

  // Student Invite Modal State
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteIdentifier, setInviteIdentifier] = useState("");
  const [inviteGrade, setInviteGrade] = useState("Grade 10");
  const [inviteClassroomId, setInviteClassroomId] = useState("");
  const [inviteParentName, setInviteParentName] = useState("");
  const [inviteParentEmail, setInviteParentEmail] = useState("");
  const [inviteParentPhone, setInviteParentPhone] = useState("");
  const [inviting, setInviting] = useState(false);
  const [inviteModalError, setInviteModalError] = useState("");
  const [inviteModalSuccess, setInviteModalSuccess] = useState("");

  // Student Edit Modal State
  const [showEditStudentModal, setShowEditStudentModal] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [editGrade, setEditGrade] = useState("Grade 10");
  const [editClassroomIds, setEditClassroomIds] = useState([]);
  const [editParentName, setEditParentName] = useState("");
  const [editParentEmail, setEditParentEmail] = useState("");
  const [editParentPhone, setEditParentPhone] = useState("");
  const [editStatus, setEditStatus] = useState("active");
  const [savingStudent, setSavingStudent] = useState(false);
  const [editStudentError, setEditStudentError] = useState("");
  const [editStudentSuccess, setEditStudentSuccess] = useState("");

  // Classroom Modal State (Create / Edit)
  const [showClassModal, setShowClassModal] = useState(false);
  const [editingClassroom, setEditingClassroom] = useState(null);
  const [className, setClassName] = useState("");
  const [classSubject, setClassSubject] = useState("Mathematics");
  const [classGrade, setClassGrade] = useState("Grade 11");
  const [classType, setClassType] = useState("group"); // "group" | "one_on_one"
  const [classDescription, setClassDescription] = useState("");
  const [classStudentUids, setClassStudentUids] = useState([]);
  const [savingClass, setSavingClass] = useState(false);
  const [classModalError, setClassModalError] = useState("");
  const [classModalSuccess, setClassModalSuccess] = useState("");

  // Copy code feedback
  const [copiedCode, setCopiedCode] = useState(false);

  const loadData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const [resStudents, resProfile, resClassrooms] = await Promise.all([
        fetch(`${apiBase}/api/tutor/students`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${apiBase}/api/tutor/profile`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${apiBase}/api/tutor/classrooms`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      if (!resStudents.ok) {
        const body = await resStudents.json().catch(() => ({}));
        throw new Error(body.error || "Failed to load student roster");
      }

      const dataStudents = await resStudents.json();
      const dataProfile = resProfile.ok ? await resProfile.json() : null;
      const dataClassrooms = resClassrooms.ok ? await resClassrooms.json() : { classrooms: [] };

      setStudents(dataStudents.students || []);
      setTutorProfile(dataProfile?.tutor || null);
      setClassrooms(dataClassrooms.classrooms || []);
    } catch (err) {
      setError(err.message || "Failed to load students and classrooms");
    } finally {
      setLoading(false);
    }
  }, [apiBase, token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleInviteStudent = async (e) => {
    e.preventDefault();
    if (!inviteIdentifier.trim()) {
      setInviteModalError("Please enter a username or email");
      return;
    }

    setInviting(true);
    setInviteModalError("");
    setInviteModalSuccess("");

    try {
      const res = await fetch(`${apiBase}/api/tutor/students/invite`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          identifier: inviteIdentifier.trim(),
          grade: inviteGrade,
          classroomId: inviteClassroomId || undefined,
          parentName: inviteParentName.trim(),
          parentEmail: inviteParentEmail.trim(),
          parentPhone: inviteParentPhone.trim()
        })
      });

      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error || "Failed to invite student");
      }

      setInviteModalSuccess(body.message || "Student added to roster!");
      setInviteIdentifier("");
      setInviteParentName("");
      setInviteParentEmail("");
      setInviteParentPhone("");
      loadData();
      setTimeout(() => {
        if (!inviteModalError) setShowInviteModal(false);
        setInviteModalSuccess("");
      }, 1600);
    } catch (err) {
      setInviteModalError(err.message || "Failed to invite student");
    } finally {
      setInviting(false);
    }
  };

  const handleOpenEditStudentModal = (st) => {
    setEditingStudent(st);
    setEditGrade(st.grade && st.grade !== "—" ? st.grade : "Grade 10");
    setEditParentName(st.parentName || "");
    setEditParentEmail(st.parentEmail || "");
    setEditParentPhone(st.parentPhone || "");
    setEditStatus(st.status === "inactive" ? "inactive" : "active");
    // Find all classrooms that contain this studentUid
    const enrolledIds = classrooms
      .filter((cls) => Array.isArray(cls.studentUids) && cls.studentUids.map(String).includes(String(st.studentUid)))
      .map((cls) => String(cls._id));
    setEditClassroomIds(enrolledIds);
    setEditStudentError("");
    setEditStudentSuccess("");
    setShowEditStudentModal(true);
  };

  const handleSaveEditStudent = async (e) => {
    e.preventDefault();
    if (!editingStudent) return;
    setSavingStudent(true);
    setEditStudentError("");
    setEditStudentSuccess("");

    try {
      const res = await fetch(`${apiBase}/api/tutor/students/${editingStudent.linkId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          parentName: editParentName.trim(),
          parentEmail: editParentEmail.trim(),
          parentPhone: editParentPhone.trim(),
          grade: editGrade,
          classroomIds: editClassroomIds,
          status: editStatus
        })
      });

      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error || "Failed to update student details");
      }

      setEditStudentSuccess("Student details updated successfully!");
      loadData();
      setTimeout(() => {
        setShowEditStudentModal(false);
        setEditStudentSuccess("");
      }, 1200);
    } catch (err) {
      setEditStudentError(err.message || "Failed to update student details");
    } finally {
      setSavingStudent(false);
    }
  };

  const handleToggleStudentStatus = async (linkId, currentStatus) => {
    const nextStatus = currentStatus === "active" ? "inactive" : "active";
    try {
      const res = await fetch(`${apiBase}/api/tutor/students/${linkId}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: nextStatus })
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Failed to update student status");
      }
      setStudents((prev) =>
        prev.map((s) => (s.linkId === linkId ? { ...s, status: nextStatus } : s))
      );
    } catch (err) {
      alert(err.message || "Could not toggle student status");
    }
  };

  const handleRemoveStudent = async (linkId, studentName) => {
    if (!window.confirm(`Are you sure you want to remove ${studentName || "this student"} from your classroom roster?`)) {
      return;
    }

    try {
      const res = await fetch(`${apiBase}/api/tutor/students/${linkId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        throw new Error("Failed to remove student");
      }
      loadData();
    } catch (err) {
      alert(err.message || "Could not remove student");
    }
  };

  const openCreateClassModal = () => {
    setEditingClassroom(null);
    setClassName("");
    setClassSubject("Mathematics");
    setClassGrade("Grade 11");
    setClassType("group");
    setClassDescription("");
    setClassStudentUids([]);
    setClassModalError("");
    setClassModalSuccess("");
    setShowClassModal(true);
  };

  const openEditClassModal = (cls) => {
    setEditingClassroom(cls);
    setClassName(cls.name || "");
    setClassSubject(cls.subject || "Mathematics");
    setClassGrade(cls.grade || "Grade 11");
    setClassType(cls.classType || "group");
    setClassDescription(cls.description || "");
    setClassStudentUids(Array.isArray(cls.studentUids) ? cls.studentUids : []);
    setClassModalError("");
    setClassModalSuccess("");
    setShowClassModal(true);
  };

  const handleSaveClassroom = async (e) => {
    e.preventDefault();
    if (!className.trim()) {
      setClassModalError("Class name is required (e.g. Algebra, Calculus)");
      return;
    }

    setSavingClass(true);
    setClassModalError("");
    setClassModalSuccess("");

    try {
      const url = editingClassroom
        ? `${apiBase}/api/tutor/classrooms/${editingClassroom._id || editingClassroom.id}`
        : `${apiBase}/api/tutor/classrooms`;
      const method = editingClassroom ? "PATCH" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: className.trim(),
          subject: classSubject.trim(),
          grade: classGrade.trim(),
          classType: classType || "group",
          description: classDescription.trim(),
          studentUids: classStudentUids
        })
      });

      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error || "Failed to save classroom");
      }

      setClassModalSuccess(body.message || "Classroom saved successfully!");
      loadData();
      setTimeout(() => {
        setShowClassModal(false);
        setClassModalSuccess("");
      }, 1200);
    } catch (err) {
      setClassModalError(err.message || "Failed to save classroom");
    } finally {
      setSavingClass(false);
    }
  };

  const handleArchiveClassroom = async (clsId, clsName) => {
    if (!window.confirm(`Are you sure you want to archive "${clsName}"? Enrolled students will remain in your global roster.`)) {
      return;
    }

    try {
      const res = await fetch(`${apiBase}/api/tutor/classrooms/${clsId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        throw new Error("Failed to archive classroom");
      }
      loadData();
    } catch (err) {
      alert(err.message || "Failed to archive classroom");
    }
  };

  const toggleStudentInClass = (uid) => {
    setClassStudentUids((prev) =>
      prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid]
    );
  };

  const inviteCode = tutorProfile?.tutorProfile?.inviteCode || "";

  const handleCopyCode = () => {
    if (!inviteCode) return;
    navigator.clipboard.writeText(inviteCode);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2500);
  };

  // Build mapping from studentUid -> array of classroom names
  const studentClassesMap = new Map();
  classrooms.forEach((c) => {
    (c.studentUids || []).forEach((uid) => {
      if (!studentClassesMap.has(uid)) studentClassesMap.set(uid, []);
      studentClassesMap.get(uid).push(c.name);
    });
  });

  const filteredStudents = useMemo(() => {
    if (rosterFilter === "active") return students.filter((s) => s.status === "active");
    if (rosterFilter === "inactive") return students.filter((s) => s.status === "inactive");
    return students;
  }, [students, rosterFilter]);

  return (
    <div className="tutor-students-page" style={{ padding: "28px 24px", maxWidth: "1240px", margin: "0 auto" }}>
      {/* ── Top Header ── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "16px",
          marginBottom: "20px"
        }}
      >
        <div>
          <h1 style={{ fontSize: "26px", fontWeight: "700", margin: "0 0 4px 0", color: "#0f172a" }}>
            Classrooms & Roster
          </h1>
          <p style={{ margin: 0, fontSize: "14px", color: "var(--muted)" }}>
            Organize students into classes (e.g. Algebra, Calculus), manage cohorts, and dispatch tests & assignments.
          </p>
        </div>

        <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
          {/* Invite Code Box */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              backgroundColor: "#f8fafc",
              border: "1px solid var(--border)",
              padding: "6px 12px",
              borderRadius: "8px"
            }}
          >
            <span style={{ fontSize: "12px", color: "var(--muted)", textTransform: "uppercase" }}>Tutor Code:</span>
            <span style={{ fontFamily: "monospace", fontWeight: "700", color: "#4f46e5", fontSize: "15px" }}>
              {inviteCode || "—"}
            </span>
            <button
              type="button"
              className="secondary-button"
              onClick={handleCopyCode}
              style={{ fontSize: "12px", padding: "4px 8px" }}
            >
              {copiedCode ? "Copied! ✓" : "Copy"}
            </button>
          </div>

          <button
            type="button"
            className="secondary-button"
            onClick={() => navigate("/tutor/performance")}
            style={{ fontSize: "13px", padding: "8px 16px", display: "flex", alignItems: "center", gap: "6px" }}
          >
            📊 Parent Reports
          </button>

          <button
            type="button"
            className="secondary-button"
            onClick={openCreateClassModal}
            style={{ fontSize: "13px", padding: "8px 16px", display: "flex", alignItems: "center", gap: "6px" }}
          >
            + Create Class
          </button>

          <button
            type="button"
            className="primary-button"
            onClick={() => {
              setInviteModalError("");
              setInviteModalSuccess("");
              setShowInviteModal(true);
            }}
            style={{ fontSize: "13px", padding: "8px 16px" }}
          >
            + Add Student
          </button>
        </div>
      </div>

      {/* ── Tabs: Student Roster vs Classes & Batches ── */}
      <div
        style={{
          display: "flex",
          gap: "8px",
          borderBottom: "1px solid var(--border)",
          marginBottom: "24px"
        }}
      >
        <button
          type="button"
          onClick={() => setActiveTab("roster")}
          style={{
            padding: "10px 18px",
            border: "none",
            background: "none",
            cursor: "pointer",
            fontWeight: "600",
            fontSize: "14px",
            color: activeTab === "roster" ? "#4f46e5" : "var(--muted)",
            borderBottom: activeTab === "roster" ? "2px solid #4f46e5" : "2px solid transparent",
            display: "flex",
            alignItems: "center",
            gap: "8px"
          }}
        >
          <span>Student Roster</span>
          <span
            style={{
              fontSize: "11px",
              padding: "2px 7px",
              borderRadius: "12px",
              backgroundColor: activeTab === "roster" ? "#ede9fe" : "#f1f5f9",
              color: activeTab === "roster" ? "#6d28d9" : "#64748b"
            }}
          >
            {students.length}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("classes")}
          style={{
            padding: "10px 18px",
            border: "none",
            background: "none",
            cursor: "pointer",
            fontWeight: "600",
            fontSize: "14px",
            color: activeTab === "classes" ? "#4f46e5" : "var(--muted)",
            borderBottom: activeTab === "classes" ? "2px solid #4f46e5" : "2px solid transparent",
            display: "flex",
            alignItems: "center",
            gap: "8px"
          }}
        >
          <span>Classes & Batches</span>
          <span
            style={{
              fontSize: "11px",
              padding: "2px 7px",
              borderRadius: "12px",
              backgroundColor: activeTab === "classes" ? "#ede9fe" : "#f1f5f9",
              color: activeTab === "classes" ? "#6d28d9" : "#64748b"
            }}
          >
            {classrooms.length}
          </span>
        </button>
      </div>

      {error && (
        <div className="error-text" style={{ padding: "12px 16px", backgroundColor: "#fee2e2", borderRadius: "8px", marginBottom: "20px" }}>
          {error}
        </div>
      )}

      {loading ? (
        <div className="analysis-box">
          <div className="question-meta">Loading roster and classes...</div>
        </div>
      ) : activeTab === "classes" ? (
        /* ── CLASSES TAB VIEW ── */
        <div>
          {classrooms.length === 0 ? (
            <div
              style={{
                backgroundColor: "#ffffff",
                border: "1px dashed var(--border)",
                borderRadius: "16px",
                padding: "48px 24px",
                textAlign: "center"
              }}
            >
              <div style={{ fontSize: "42px", marginBottom: "12px" }}>🏫</div>
              <h3 style={{ fontSize: "18px", fontWeight: "600", margin: "0 0 8px 0" }}>No classes created yet</h3>
              <p style={{ color: "var(--muted)", fontSize: "14px", maxWidth: "480px", margin: "0 auto 20px auto" }}>
                Group your students into specific classes like <strong>Algebra</strong> or <strong>Calculus</strong> so you can
                target homework and timed exams accurately.
              </p>
              <button
                type="button"
                className="primary-button"
                onClick={openCreateClassModal}
                style={{ padding: "10px 22px" }}
              >
                + Create Your First Class
              </button>
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
                gap: "20px"
              }}
            >
              {classrooms.map((cls) => {
                const enrolledCount = (cls.studentUids || []).length;
                return (
                  <div
                    key={cls._id || cls.id}
                    style={{
                      backgroundColor: "#ffffff",
                      border: "1px solid var(--border)",
                      borderRadius: "14px",
                      padding: "20px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
                    }}
                  >
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px", flexWrap: "wrap" }}>
                            <span
                              style={{
                                fontSize: "11px",
                                fontWeight: "600",
                                color: cls.classType === "one_on_one" ? "#0369a1" : "#6d28d9",
                                backgroundColor: cls.classType === "one_on_one" ? "#e0f2fe" : "#ede9fe",
                                padding: "2px 8px",
                                borderRadius: "6px",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px"
                              }}
                            >
                              {cls.classType === "one_on_one" ? "👤 1:1 Private" : "👥 Group Batch"}
                            </span>
                            <span
                              style={{
                                fontSize: "11px",
                                fontWeight: "500",
                                color: "#475569",
                                backgroundColor: "#f1f5f9",
                                padding: "2px 8px",
                                borderRadius: "6px"
                              }}
                            >
                              {cls.grade || "Grade 11"} • {cls.subject || "Mathematics"}
                            </span>
                          </div>
                          <h3 style={{ fontSize: "18px", fontWeight: "700", margin: "4px 0", color: "#0f172a" }}>
                            {cls.name}
                          </h3>
                        </div>
                        <div style={{ display: "flex", gap: "4px" }}>
                          <button
                            type="button"
                            className="secondary-button"
                            onClick={() => openEditClassModal(cls)}
                            style={{ fontSize: "12px", padding: "4px 8px" }}
                            title="Edit class & members"
                          >
                            ✏️ Edit
                          </button>
                          <button
                            type="button"
                            className="secondary-button"
                            onClick={() => handleArchiveClassroom(cls._id || cls.id, cls.name)}
                            style={{ fontSize: "12px", padding: "4px 8px", color: "#dc2626", borderColor: "#fca5a5" }}
                            title="Archive class"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>

                      {cls.description && (
                        <p style={{ fontSize: "13px", color: "var(--muted)", margin: "0 0 14px 0", lineHeight: "1.4" }}>
                          {cls.description}
                        </p>
                      )}

                      <div
                        style={{
                          backgroundColor: "#f8fafc",
                          borderRadius: "8px",
                          padding: "10px 14px",
                          marginBottom: "16px"
                        }}
                      >
                        <div style={{ fontSize: "12px", fontWeight: "600", color: "#334155", marginBottom: "4px" }}>
                          👥 {enrolledCount} Enrolled Student{enrolledCount === 1 ? "" : "s"}
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                          {cls.students && cls.students.length > 0 ? (
                            cls.students.map((st) => (
                              <span
                                key={st.uid}
                                style={{
                                   fontSize: "11px",
                                  backgroundColor: "#ffffff",
                                  border: "1px solid #cbd5e1",
                                  padding: "2px 8px",
                                  borderRadius: "12px",
                                  color: "#334155"
                                }}
                              >
                                {st.name || st.username || st.email}
                              </span>
                            ))
                          ) : (
                            <span style={{ fontSize: "12px", color: "var(--muted)" }}>No students mapped yet</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: "8px", borderTop: "1px solid var(--border)", paddingTop: "14px" }}>
                      <button
                        type="button"
                        className="secondary-button"
                        onClick={() => navigate("/tutor/calendar", { state: { classroomId: cls._id || cls.id } })}
                        style={{ fontSize: "12px", padding: "6px 14px" }}
                      >
                        Schedule Class
                      </button>
                      <button
                        type="button"
                        className="primary-button"
                        onClick={() => navigate("/my-questions", { state: { preselectClassroomId: cls._id || cls.id } })}
                        style={{ fontSize: "12px", padding: "6px 14px", flex: 1, textAlign: "center" }}
                      >
                        Assign to Class
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        /* ── ALL STUDENTS (ROSTER) TAB VIEW ── */
        students.length === 0 ? (
          <div
            style={{
              backgroundColor: "#ffffff",
              border: "1px dashed var(--border)",
              borderRadius: "16px",
              padding: "48px 24px",
              textAlign: "center"
            }}
          >
            <div style={{ fontSize: "42px", marginBottom: "12px" }}>👥</div>
            <h3 style={{ fontSize: "18px", fontWeight: "600", margin: "0 0 8px 0" }}>No students linked yet</h3>
            <p style={{ color: "var(--muted)", fontSize: "14px", maxWidth: "480px", margin: "0 auto 20px auto" }}>
              Share your tutor code <strong>{inviteCode}</strong> with your students to have them connect instantly, or manually
              invite them by email/username.
            </p>
            <button
              type="button"
              className="primary-button"
              onClick={() => setShowInviteModal(true)}
              style={{ padding: "10px 22px" }}
            >
              + Add Your First Student
            </button>
          </div>
        ) : (
          <div>
            {/* Status Filter Bar */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", flexWrap: "wrap", gap: "10px" }}>
              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <span style={{ fontSize: "13px", fontWeight: "600", color: "#475569" }}>Status:</span>
                <button
                  type="button"
                  onClick={() => setRosterFilter("all")}
                  style={{
                    padding: "4px 12px",
                    fontSize: "12px",
                    borderRadius: "20px",
                    border: rosterFilter === "all" ? "1.5px solid #4f46e5" : "1px solid #cbd5e1",
                    backgroundColor: rosterFilter === "all" ? "#ede9fe" : "#ffffff",
                    color: rosterFilter === "all" ? "#4f46e5" : "#64748b",
                    fontWeight: "600",
                    cursor: "pointer"
                  }}
                >
                  All ({students.length})
                </button>
                <button
                  type="button"
                  onClick={() => setRosterFilter("active")}
                  style={{
                    padding: "4px 12px",
                    fontSize: "12px",
                    borderRadius: "20px",
                    border: rosterFilter === "active" ? "1.5px solid #059669" : "1px solid #cbd5e1",
                    backgroundColor: rosterFilter === "active" ? "#ecfdf5" : "#ffffff",
                    color: rosterFilter === "active" ? "#059669" : "#64748b",
                    fontWeight: "600",
                    cursor: "pointer"
                  }}
                >
                  Active ({students.filter((s) => s.status === "active").length})
                </button>
                <button
                  type="button"
                  onClick={() => setRosterFilter("inactive")}
                  style={{
                    padding: "4px 12px",
                    fontSize: "12px",
                    borderRadius: "20px",
                    border: rosterFilter === "inactive" ? "1.5px solid #d97706" : "1px solid #cbd5e1",
                    backgroundColor: rosterFilter === "inactive" ? "#fffbeb" : "#ffffff",
                    color: rosterFilter === "inactive" ? "#d97706" : "#64748b",
                    fontWeight: "600",
                    cursor: "pointer"
                  }}
                >
                  Paused ({students.filter((s) => s.status === "inactive").length})
                </button>
              </div>
              <div style={{ fontSize: "12px", color: "var(--muted)" }}>
                Paused students will not receive class-wide homework or tests.
              </div>
            </div>

            <div
              style={{
                backgroundColor: "#ffffff",
                border: "1px solid var(--border)",
                borderRadius: "14px",
                overflow: "visible",
                boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: "14px" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f8fafc", borderBottom: "1px solid var(--border)", color: "var(--muted)", fontSize: "12px", textTransform: "uppercase" }}>
                    <th style={{ padding: "14px 16px" }}>Student</th>
                    <th style={{ padding: "14px 16px" }}>Grade / School</th>
                    <th style={{ padding: "14px 16px" }}>Enrolled Batches</th>
                    <th style={{ padding: "14px 16px" }}>Parent Contact</th>
                    <th style={{ padding: "14px 16px" }}>Submission Mode</th>
                    <th style={{ padding: "14px 16px" }}>Completed Problems</th>
                    <th style={{ padding: "14px 16px" }}>Status</th>
                    <th style={{ padding: "14px 16px", textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents.length === 0 ? (
                    <tr>
                      <td colSpan={8} style={{ padding: "32px 20px", textAlign: "center", color: "var(--muted)" }}>
                        No students found in this status filter.
                      </td>
                    </tr>
                  ) : (
                    filteredStudents.map((st) => {
                      const enrolledClasses = studentClassesMap.get(st.studentUid) || [];
                      const isMenuOpen = openStatusMenuId === st.linkId;
                      return (
                        <tr key={st.linkId} style={{ borderBottom: "1px solid var(--border)" }}>
                          <td style={{ padding: "16px 16px" }}>
                            <div style={{ fontWeight: "600", color: "#0f172a" }}>{st.name}</div>
                            <div style={{ fontSize: "12px", color: "var(--muted)" }}>
                              {st.username ? `@${st.username}` : st.email}
                            </div>
                          </td>

                          <td style={{ padding: "16px 16px" }}>
                            <div style={{ color: "#334155", fontWeight: "600" }}>{st.grade || "—"}</div>
                            <div style={{ fontSize: "12px", color: "var(--muted)" }}>{st.schoolName || ""}</div>
                          </td>

                          <td style={{ padding: "16px 16px" }}>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                              {enrolledClasses.length > 0 ? (
                                enrolledClasses.map((cName, idx) => (
                                  <span
                                    key={idx}
                                    style={{
                                      backgroundColor: "#ede9fe",
                                      color: "#6d28d9",
                                      fontSize: "11px",
                                      fontWeight: "600",
                                      padding: "2px 8px",
                                      borderRadius: "12px"
                                    }}
                                  >
                                    {cName}
                                  </span>
                                ))
                              ) : (
                                <span style={{ fontSize: "12px", color: "var(--muted)" }}>—</span>
                              )}
                            </div>
                          </td>

                          <td style={{ padding: "16px 16px" }}>
                            {st.parentEmail ? (
                              <div>
                                {st.parentName && (
                                  <div style={{ fontWeight: "600", color: "#0f172a", fontSize: "13px" }}>
                                    {st.parentName}
                                  </div>
                                )}
                                <div style={{ fontSize: "12px", color: "#2563eb", fontWeight: "500" }}>
                                  ✉️ {st.parentEmail}
                                </div>
                                {st.parentPhone && (
                                  <div style={{ fontSize: "11px", color: "#64748b" }}>
                                    📞 {st.parentPhone}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span style={{ fontSize: "12px", color: "var(--muted)" }}>— Not set —</span>
                            )}
                          </td>

                          <td style={{ padding: "16px 16px" }}>
                            {st.preferredSubmissionMode === "slate" && (
                              <span style={{ fontSize: "12px", color: "#1d4ed8", backgroundColor: "#eff6ff", padding: "2px 8px", borderRadius: "6px", fontWeight: "600" }}>Slate ({st.slateCount})</span>
                            )}
                            {st.preferredSubmissionMode === "photo_upload" && (
                              <span style={{ fontSize: "12px", color: "#b45309", backgroundColor: "#fffbeb", padding: "2px 8px", borderRadius: "6px", fontWeight: "600" }}>Photo Upload ({st.photoCount})</span>
                            )}
                            {st.preferredSubmissionMode === "hybrid" && (
                              <span style={{ fontSize: "12px", color: "#7c3aed", backgroundColor: "#faf5ff", padding: "2px 8px", borderRadius: "6px", fontWeight: "600" }}>
                                Mixed ({st.slateCount} slate / {st.photoCount} photo)
                              </span>
                            )}
                            {st.preferredSubmissionMode === "undecided" && (
                              <span style={{ fontSize: "12px", color: "var(--muted)" }}>No submissions</span>
                            )}
                          </td>

                          <td style={{ padding: "16px 16px" }}>
                            <span style={{ fontWeight: "600", color: "#0f172a" }}>{st.completedAttempts}</span>
                            <span style={{ color: "var(--muted)", fontSize: "12px" }}> / {st.totalAttempts} total</span>
                          </td>

                          {/* Status */}
                          <td style={{ padding: "16px 16px", position: "relative" }}>
                            <div style={{ display: "inline-block", position: "relative" }}>
                              <button
                                type="button"
                                onClick={() => setOpenStatusMenuId(isMenuOpen ? null : st.linkId)}
                                title="Click to manage student status"
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  padding: "5px 12px",
                                  borderRadius: "20px",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  cursor: "pointer",
                                  border: st.status === "active" ? "1.5px solid #86efac" : "1.5px solid #fde68a",
                                  backgroundColor: st.status === "active" ? "#f0fdf4" : "#fffbeb",
                                  color: st.status === "active" ? "#166534" : "#92400e"
                                }}
                              >
                                <span style={{ width: "7px", height: "7px", borderRadius: "50%", backgroundColor: st.status === "active" ? "#16a34a" : "#d97706" }} />
                                <span>{st.status === "active" ? "Active" : "Paused"}</span>
                                <span style={{ fontSize: "9px", color: "#64748b", marginLeft: "2px" }}>▼</span>
                              </button>

                              {isMenuOpen && (
                                <>
                                  <div
                                    onClick={() => setOpenStatusMenuId(null)}
                                    style={{ position: "fixed", inset: 0, zIndex: 40 }}
                                  />
                                  <div
                                    style={{
                                      position: "absolute",
                                      left: 0,
                                      top: "100%",
                                      marginTop: "6px",
                                      backgroundColor: "#ffffff",
                                      borderRadius: "8px",
                                      boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)",
                                      border: "1px solid #e2e8f0",
                                      zIndex: 50,
                                      minWidth: "180px",
                                      overflow: "hidden",
                                      textAlign: "left"
                                    }}
                                  >
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setOpenStatusMenuId(null);
                                        handleOpenEditStudentModal(st);
                                      }}
                                      style={{
                                        display: "block",
                                        width: "100%",
                                        textAlign: "left",
                                        padding: "9px 14px",
                                        fontSize: "13px",
                                        fontWeight: "600",
                                        backgroundColor: "transparent",
                                        border: "none",
                                        cursor: "pointer",
                                        color: "#4f46e5"
                                      }}
                                      onMouseEnter={(e) => (e.target.style.backgroundColor = "#f8fafc")}
                                      onMouseLeave={(e) => (e.target.style.backgroundColor = "transparent")}
                                    >
                                      ✏️ Edit Student Details
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setOpenStatusMenuId(null);
                                        handleToggleStudentStatus(st.linkId, st.status);
                                      }}
                                      style={{
                                        display: "block",
                                        width: "100%",
                                        textAlign: "left",
                                        padding: "9px 14px",
                                        fontSize: "13px",
                                        fontWeight: "600",
                                        backgroundColor: "transparent",
                                        border: "none",
                                        cursor: "pointer",
                                        color: st.status === "active" ? "#d97706" : "#16a34a"
                                      }}
                                      onMouseEnter={(e) => (e.target.style.backgroundColor = "#f8fafc")}
                                      onMouseLeave={(e) => (e.target.style.backgroundColor = "transparent")}
                                    >
                                      {st.status === "active" ? "Pause Student" : "Activate Student"}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setOpenStatusMenuId(null);
                                        navigate("/tutor/calendar", { state: { studentUid: st.studentUid } });
                                      }}
                                      style={{
                                        display: "block",
                                        width: "100%",
                                        textAlign: "left",
                                        padding: "9px 14px",
                                        fontSize: "13px",
                                        fontWeight: "500",
                                        backgroundColor: "transparent",
                                        border: "none",
                                        cursor: "pointer",
                                        color: "#334155"
                                      }}
                                      onMouseEnter={(e) => (e.target.style.backgroundColor = "#f8fafc")}
                                      onMouseLeave={(e) => (e.target.style.backgroundColor = "transparent")}
                                    >
                                      Schedule 1:1 Class
                                    </button>
                                    <div style={{ height: "1px", backgroundColor: "#f1f5f9" }} />
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setOpenStatusMenuId(null);
                                        handleRemoveStudent(st.linkId || st.studentUid, st.name);
                                      }}
                                      style={{
                                        display: "block",
                                        width: "100%",
                                        textAlign: "left",
                                        padding: "9px 14px",
                                        fontSize: "13px",
                                        fontWeight: "600",
                                        backgroundColor: "transparent",
                                        border: "none",
                                        cursor: "pointer",
                                        color: "#dc2626"
                                      }}
                                      onMouseEnter={(e) => (e.target.style.backgroundColor = "#fef2f2")}
                                      onMouseLeave={(e) => (e.target.style.backgroundColor = "transparent")}
                                    >
                                      Remove from Roster
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          </td>

                          {/* Actions: Edit Button */}
                          <td style={{ padding: "16px 16px", textAlign: "right" }}>
                            <button
                              type="button"
                              className="secondary-button"
                              onClick={() => handleOpenEditStudentModal(st)}
                              style={{ fontSize: "12px", padding: "5px 12px", display: "inline-flex", alignItems: "center", gap: "4px" }}
                              title="Edit student details, parent contact, class, and grade"
                            >
                              ✏️ Edit
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}

      {/* ── Add Student Modal ── */}
      {showInviteModal && (
        <div
          className="feedback-modal"
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px"
          }}
        >
          <div
            className="feedback-card"
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              padding: "26px",
              maxWidth: "520px",
              width: "100%",
              maxHeight: "90vh",
              overflowY: "auto",
              boxShadow: "0 20px 25px -5px rgba(0,0,0,0.2)"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <h2 style={{ fontSize: "20px", fontWeight: "700", margin: 0, color: "#0f172a" }}>Add Student to Roster</h2>
              <button
                type="button"
                onClick={() => setShowInviteModal(false)}
                style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: "var(--muted)" }}
              >
                ✕
              </button>
            </div>

            <p style={{ fontSize: "13px", color: "var(--muted)", margin: "0 0 16px 0" }}>
              Add a student by their username or email address and configure grade, class, and parent contact info.
            </p>

            {inviteModalError && (
              <div className="error-text" style={{ padding: "10px 14px", backgroundColor: "#fee2e2", borderRadius: "8px", marginBottom: "14px", fontSize: "13px" }}>
                {inviteModalError}
              </div>
            )}
            {inviteModalSuccess && (
              <div style={{ padding: "10px 14px", backgroundColor: "#d1fae5", color: "#065f46", borderRadius: "8px", marginBottom: "14px", fontSize: "13px", fontWeight: "500" }}>
                ✓ {inviteModalSuccess}
              </div>
            )}

            <form onSubmit={handleInviteStudent}>
              <div style={{ marginBottom: "14px" }}>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "5px" }}>
                  Student Username or Email *
                </label>
                <input
                  type="text"
                  required
                  value={inviteIdentifier}
                  onChange={(e) => setInviteIdentifier(e.target.value)}
                  placeholder="e.g. rahul.solves or student@gmail.com"
                  style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1px solid var(--border)", fontSize: "14px", boxSizing: "border-box" }}
                  autoFocus
                />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "14px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "5px" }}>
                    Grade
                  </label>
                  <select
                    value={inviteGrade}
                    onChange={(e) => setInviteGrade(e.target.value)}
                    style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1px solid var(--border)", fontSize: "14px", background: "#ffffff", boxSizing: "border-box" }}
                  >
                    {["Grade 6", "Grade 7", "Grade 8", "Grade 9", "Grade 10", "Grade 11", "Grade 12", "Undergraduate", "Other"].map((g) => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "5px" }}>
                    Assign to Class (Optional)
                  </label>
                  <select
                    value={inviteClassroomId}
                    onChange={(e) => setInviteClassroomId(e.target.value)}
                    style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1px solid var(--border)", fontSize: "14px", background: "#ffffff", boxSizing: "border-box" }}
                  >
                    <option value="">No Class (Unassigned)</option>
                    {classrooms.map((cls) => (
                      <option key={cls._id} value={cls._id}>{cls.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Parent Contact Details */}
              <div style={{ background: "#f8fafc", padding: "14px", borderRadius: "10px", border: "1px solid #e2e8f0", marginBottom: "18px" }}>
                <div style={{ fontSize: "12px", fontWeight: "700", color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "8px" }}>
                  👨‍👩‍👦 Parent / Guardian Details (Optional)
                </div>

                <div style={{ marginBottom: "10px" }}>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#334155", marginBottom: "4px" }}>
                    Parent Name
                  </label>
                  <input
                    type="text"
                    value={inviteParentName}
                    onChange={(e) => setInviteParentName(e.target.value)}
                    placeholder="e.g. Mrs. Sharma"
                    style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "13px", boxSizing: "border-box" }}
                  />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#334155", marginBottom: "4px" }}>
                      Parent Email
                    </label>
                    <input
                      type="email"
                      value={inviteParentEmail}
                      onChange={(e) => setInviteParentEmail(e.target.value)}
                      placeholder="parent@example.com"
                      style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "13px", boxSizing: "border-box" }}
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#334155", marginBottom: "4px" }}>
                      Parent Contact / Phone
                    </label>
                    <input
                      type="tel"
                      value={inviteParentPhone}
                      onChange={(e) => setInviteParentPhone(e.target.value)}
                      placeholder="+91 98765 43210"
                      style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "13px", boxSizing: "border-box" }}
                    />
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setShowInviteModal(false)}
                  disabled={inviting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="primary-button"
                  disabled={inviting || !inviteIdentifier.trim()}
                >
                  {inviting ? "Adding..." : "Add to Roster"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Edit Student Details Modal ── */}
      {showEditStudentModal && editingStudent && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(15, 23, 42, 0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px"
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              padding: "26px",
              maxWidth: "540px",
              width: "100%",
              maxHeight: "90vh",
              overflowY: "auto",
              boxShadow: "0 20px 25px -5px rgba(0,0,0,0.2)"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <h2 style={{ fontSize: "20px", fontWeight: "700", margin: 0, color: "#0f172a" }}>Edit Student Details</h2>
              <button
                type="button"
                onClick={() => setShowEditStudentModal(false)}
                style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: "var(--muted)" }}
              >
                ✕
              </button>
            </div>

            <p style={{ fontSize: "13px", color: "var(--muted)", margin: "0 0 16px 0" }}>
              Student name and account credentials are fixed. You can update grade, enrolled batches, parent contact information, and roster status.
            </p>

            {editStudentError && (
              <div style={{ padding: "10px 14px", backgroundColor: "#fee2e2", color: "#991b1b", borderRadius: "8px", marginBottom: "14px", fontSize: "13px" }}>
                {editStudentError}
              </div>
            )}
            {editStudentSuccess && (
              <div style={{ padding: "10px 14px", backgroundColor: "#d1fae5", color: "#065f46", borderRadius: "8px", marginBottom: "14px", fontSize: "13px", fontWeight: "600" }}>
                ✓ {editStudentSuccess}
              </div>
            )}

            <form onSubmit={handleSaveEditStudent}>
              {/* Fixed Identity Fields */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "14px", background: "#f8fafc", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#64748b", marginBottom: "4px" }}>
                    Student Name (Fixed)
                  </label>
                  <input
                    type="text"
                    disabled
                    value={editingStudent.name}
                    style={{ width: "100%", padding: "8px 10px", borderRadius: "6px", border: "1px solid #cbd5e1", background: "#f1f5f9", color: "#475569", fontSize: "13px", cursor: "not-allowed", boxSizing: "border-box" }}
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#64748b", marginBottom: "4px" }}>
                    Student Email / User (Fixed)
                  </label>
                  <input
                    type="text"
                    disabled
                    value={editingStudent.email || `@${editingStudent.username}`}
                    style={{ width: "100%", padding: "8px 10px", borderRadius: "6px", border: "1px solid #cbd5e1", background: "#f1f5f9", color: "#475569", fontSize: "13px", cursor: "not-allowed", boxSizing: "border-box" }}
                  />
                </div>
              </div>

              {/* Editable Academic Details: Grade & Status */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "14px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "5px" }}>
                    Grade
                  </label>
                  <select
                    value={editGrade}
                    onChange={(e) => setEditGrade(e.target.value)}
                    style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1px solid var(--border)", fontSize: "14px", background: "#ffffff", boxSizing: "border-box" }}
                  >
                    {["Grade 6", "Grade 7", "Grade 8", "Grade 9", "Grade 10", "Grade 11", "Grade 12", "Undergraduate", "Other"].map((g) => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "5px" }}>
                    Roster Status
                  </label>
                  <select
                    value={editStatus}
                    onChange={(e) => setEditStatus(e.target.value)}
                    style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1px solid var(--border)", fontSize: "14px", background: "#ffffff", boxSizing: "border-box" }}
                  >
                    <option value="active">Active (Receives coursework)</option>
                    <option value="inactive">Paused (No new coursework)</option>
                  </select>
                </div>
              </div>

              {/* Enrolled Batches / Classes Checkboxes */}
              {classrooms.length > 0 && (
                <div style={{ marginBottom: "14px" }}>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "6px" }}>
                    Enrolled Batches / Classes
                  </label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                    {classrooms.map((cls) => {
                      const isChecked = editClassroomIds.includes(String(cls._id));
                      return (
                        <label
                          key={cls._id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            fontSize: "13px",
                            background: isChecked ? "#eff6ff" : "#f8fafc",
                            border: isChecked ? "1.5px solid #3b82f6" : "1px solid #cbd5e1",
                            padding: "6px 12px",
                            borderRadius: "8px",
                            cursor: "pointer",
                            color: isChecked ? "#1d4ed8" : "#334155",
                            fontWeight: isChecked ? "600" : "normal"
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => {
                              const strId = String(cls._id);
                              setEditClassroomIds((prev) =>
                                prev.includes(strId) ? prev.filter((id) => id !== strId) : [...prev, strId]
                              );
                            }}
                            style={{ cursor: "pointer" }}
                          />
                          {cls.name}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Editable Parent Contact Details */}
              <div style={{ background: "#f8fafc", padding: "14px", borderRadius: "10px", border: "1px solid #e2e8f0", marginBottom: "18px" }}>
                <div style={{ fontSize: "12px", fontWeight: "700", color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "8px" }}>
                  👨‍👩‍👦 Parent / Guardian Contact Details
                </div>

                <div style={{ marginBottom: "10px" }}>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#334155", marginBottom: "4px" }}>
                    Parent Name
                  </label>
                  <input
                    type="text"
                    value={editParentName}
                    onChange={(e) => setEditParentName(e.target.value)}
                    placeholder="e.g. Mrs. Sharma"
                    style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "13px", boxSizing: "border-box" }}
                  />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#334155", marginBottom: "4px" }}>
                      Parent Email
                    </label>
                    <input
                      type="email"
                      value={editParentEmail}
                      onChange={(e) => setEditParentEmail(e.target.value)}
                      placeholder="parent@example.com"
                      style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "13px", boxSizing: "border-box" }}
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#334155", marginBottom: "4px" }}>
                      Parent Contact / Phone
                    </label>
                    <input
                      type="tel"
                      value={editParentPhone}
                      onChange={(e) => setEditParentPhone(e.target.value)}
                      placeholder="+91 98765 43210"
                      style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "13px", boxSizing: "border-box" }}
                    />
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setShowEditStudentModal(false)}
                  disabled={savingStudent}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="primary-button"
                  disabled={savingStudent}
                  style={{ background: "#4f46e5" }}
                >
                  {savingStudent ? "Saving Changes..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Create / Edit Classroom Modal ── */}
      {showClassModal && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(15, 23, 42, 0.65)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1100,
            padding: "20px"
          }}
          onClick={() => setShowClassModal(false)}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              padding: "28px",
              maxWidth: "540px",
              width: "100%",
              maxHeight: "90vh",
              overflowY: "auto",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <h2 style={{ fontSize: "20px", fontWeight: "700", margin: 0, color: "#0f172a" }}>
                {editingClassroom ? `Edit Class: ${editingClassroom.name}` : "Create New Class / Batch"}
              </h2>
              <button
                type="button"
                onClick={() => setShowClassModal(false)}
                style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: "var(--muted)" }}
              >
                ✕
              </button>
            </div>

            {classModalError && (
              <div className="error-text" style={{ padding: "10px 14px", backgroundColor: "#fee2e2", borderRadius: "8px", marginBottom: "14px" }}>
                {classModalError}
              </div>
            )}
            {classModalSuccess && (
              <div style={{ padding: "10px 14px", backgroundColor: "#d1fae5", color: "#065f46", borderRadius: "8px", marginBottom: "14px", fontSize: "13px", fontWeight: "500" }}>
                ✓ {classModalSuccess}
              </div>
            )}

            <form onSubmit={handleSaveClassroom}>
              {/* Classroom Type: Group vs 1:1 */}
              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "6px", color: "#334155" }}>
                  Classroom Format
                </label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "10px 12px",
                      borderRadius: "8px",
                      border: classType === "group" ? "2px solid #4f46e5" : "1px solid var(--border)",
                      backgroundColor: classType === "group" ? "#ede9fe" : "#ffffff",
                      cursor: "pointer",
                      fontSize: "13px",
                      fontWeight: classType === "group" ? "600" : "400",
                      color: classType === "group" ? "#4f46e5" : "#334155"
                    }}
                  >
                    <input
                      type="radio"
                      name="classType"
                      value="group"
                      checked={classType === "group"}
                      onChange={() => setClassType("group")}
                      style={{ cursor: "pointer" }}
                    />
                    <span>👥 Group Batch</span>
                  </label>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "10px 12px",
                      borderRadius: "8px",
                      border: classType === "one_on_one" ? "2px solid #0284c7" : "1px solid var(--border)",
                      backgroundColor: classType === "one_on_one" ? "#e0f2fe" : "#ffffff",
                      cursor: "pointer",
                      fontSize: "13px",
                      fontWeight: classType === "one_on_one" ? "600" : "400",
                      color: classType === "one_on_one" ? "#0284c7" : "#334155"
                    }}
                  >
                    <input
                      type="radio"
                      name="classType"
                      value="one_on_one"
                      checked={classType === "one_on_one"}
                      onChange={() => setClassType("one_on_one")}
                      style={{ cursor: "pointer" }}
                    />
                    <span>👤 1:1 Private</span>
                  </label>
                </div>
              </div>

              <div style={{ marginBottom: "14px" }}>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "4px", color: "#334155" }}>
                  Class Name *
                </label>
                <input
                  type="text"
                  value={className}
                  onChange={(e) => setClassName(e.target.value)}
                  placeholder="e.g. Algebra, Grade 11 Calculus, SAT Prep"
                  style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1px solid var(--border)", fontSize: "14px", boxSizing: "border-box" }}
                  required
                  autoFocus
                />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "14px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "4px", color: "#334155" }}>
                    Subject
                  </label>
                  <input
                    type="text"
                    value={classSubject}
                    onChange={(e) => setClassSubject(e.target.value)}
                    placeholder="e.g. Mathematics"
                    style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1px solid var(--border)", fontSize: "14px", boxSizing: "border-box" }}
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "4px", color: "#334155" }}>
                    Grade / Level
                  </label>
                  <input
                    type="text"
                    value={classGrade}
                    onChange={(e) => setClassGrade(e.target.value)}
                    placeholder="e.g. Grade 11"
                    style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1px solid var(--border)", fontSize: "14px", boxSizing: "border-box" }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "4px", color: "#334155" }}>
                  Description / Objective
                </label>
                <textarea
                  rows={2}
                  value={classDescription}
                  onChange={(e) => setClassDescription(e.target.value)}
                  placeholder="e.g. Weekly problem sets and chapter tests for algebra students"
                  style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1px solid var(--border)", fontSize: "13px", boxSizing: "border-box" }}
                />
              </div>

              {/* Student Multi-Select Checkboxes */}
              <div style={{ marginBottom: "20px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                  <label style={{ fontSize: "13px", fontWeight: "600", color: "#334155" }}>
                    Map Students to this Class ({classStudentUids.length} selected)
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      if (classStudentUids.length === students.length) {
                        setClassStudentUids([]);
                      } else {
                        setClassStudentUids(students.map((s) => s.studentUid));
                      }
                    }}
                    style={{ background: "none", border: "none", color: "#4f46e5", fontSize: "12px", cursor: "pointer", fontWeight: "500" }}
                  >
                    {classStudentUids.length === students.length ? "Deselect All" : "Select All"}
                  </button>
                </div>

                <div
                  style={{
                    maxHeight: "180px",
                    overflowY: "auto",
                    border: "1px solid var(--border)",
                    borderRadius: "8px",
                    padding: "8px 12px",
                    backgroundColor: "#f8fafc"
                  }}
                >
                  {students.length === 0 ? (
                    <div style={{ fontSize: "13px", color: "var(--muted)", padding: "8px 0" }}>
                      No students in your roster yet. Add students first to enroll them.
                    </div>
                  ) : (
                    students.map((st) => {
                      const isChecked = classStudentUids.includes(st.studentUid);
                      return (
                        <label
                          key={st.studentUid}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            padding: "6px 0",
                            borderBottom: "1px solid #f1f5f9",
                            cursor: "pointer",
                            fontSize: "13px"
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleStudentInClass(st.studentUid)}
                            style={{ cursor: "pointer" }}
                          />
                          <span style={{ fontWeight: isChecked ? "600" : "400", color: "#0f172a" }}>
                            {st.name}
                          </span>
                          <span style={{ color: "var(--muted)", fontSize: "11px" }}>
                            ({st.username ? `@${st.username}` : st.email})
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setShowClassModal(false)}
                  disabled={savingClass}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="primary-button"
                  disabled={savingClass || !className.trim()}
                >
                  {savingClass ? "Saving..." : editingClassroom ? "Update Class" : "Create Class"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

          </div>
  );
}
